import React, { useState, useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Row, Col, Form, Input, FormGroup, Button, Label, Alert, Modal, ModalHeader, ModalBody, ModalFooter, } from "reactstrap"
import Select from 'react-select'
import { BoardTypeAhead, CarTypeAhead, DriverTypeAhead, RepsTypeAhead, IncidentTypeAhead } from "./TypeAheadComponents"
import { CreateTaskMutation } from "../Mutations"
import withApolloClient from '../../withApolloClient'

const AllColumnsQuery = gql`query AllColumnsQuery($boardId: ID!){
    allColumns(boardId: $boardId, isArchived: false){
        edges {
            node {
                id
                pk
                title
                isRequired
                columnType
                elementType
                allowedOptions
                contentType {
                    id
                    appLabel
                    model
                }
            }
        }
    }
}`

function NewTaskModal({ isOpen, closeModal, boardId: parentBoard, filterValues, ...props }) {
    const [title, setTitle] = useState("")
    const [description, setDescription] = useState("")
    const [boardId, setBoardId] = useState(parentBoard ? parentBoard : "")
    const [repIds, setRepIds] = useState([])
    const [dueDate, setDueDate] = useState("")
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [columnValues, setColumnValues] = useState({})

    const { data: columnsData, loading: columnsLoading, error: columnsError } = useQuery(
        AllColumnsQuery,
        {
            variables: { boardId: boardId },
            fetchPolicy: 'network-only',
            skip: !boardId,
        }
    )

    const setColumnValue = (columnId, value) => {
        setColumnValues({ ...columnValues, [columnId]: value })
    }

    useEffect(() => {
        if (columnsData && columnsData?.allColumns?.edges?.length > 0 && filterValues?.incidentId) {
            const incidentColumn = columnsData.allColumns.edges.find(column => column.node.contentType.model === "incident")
            if (incidentColumn) {
                setColumnValue(incidentColumn.node.id, filterValues?.incidentId)
            }
        }
    }, [columnsData, filterValues?.incidentId])

    function RenderListColumnInput({ column }) {
        return ""
    }

    const createTask = (e) => {
        e.preventDefault()
        try {
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }
            if (!boardId) {
                setError("Board is required")
                return
            }
            if (!props.apolloClient) {
                throw new Error("GQL Client not found")
            }
            if (!title || title.length < 4) {
                setError("Title is required or too short")
                return
            }

            setLoading(true)

            let requiredColumnValues = columnsData?.allColumns?.edges?.filter(column => column.node.isRequired) || []

            for (const column of requiredColumnValues) {
                if (!columnValues[column.node.id]) {
                    setError(`${column.node.title} is required`)
                    return
                }
            }

            let columnValuesInput = Object.entries(columnValues).map(([columnId, value]) => ({
                columnId: columnId,
                value: value,
            }))
            const input = {
                title: title,
                description: description,
                assignedToIds: repIds,
                dueDate: dueDate || null,
                boardId: boardId,
                columnValues: columnValuesInput
            }

            props.apolloClient.mutate({
                mutation: CreateTaskMutation,
                refetchQueries: ["AllTasks"],
                variables: { input },
            }).then((result) => {
                setLoading(false)
                if (!result) {
                    throw new Error("Error creating task")
                }
                if (result.data && result.data.createTask && result.data.createTask.ok) {
                    setError("")
                    closeModal("")
                } else {
                    const errorMessage = result.data && result.data.createTask && result.data.createTask.errors && result.data.createTask.errors.messages ? result.data.createTask.errors.messages[0] : "Unknown Error"
                    throw new Error(errorMessage)
                }
            }).catch((error) => {
                setError("Error creating task " + error.toString())
                setLoading(false)
            })
        } catch (error) {
            console.log("Error creating task ", error)
            setError("Error creating task " + error.toString())
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} className="">
            <ModalHeader>Create Task</ModalHeader>
            <ModalBody>
                <Form onSubmit={createTask}>
                    <Row>
                        {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
                        <Col xs={12}><FormGroup>
                            <Label for="repIds">Board *</Label>
                            <BoardTypeAhead defaultValue={parentBoard} onChange={setBoardId} isDisabled={!!parentBoard} />
                        </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label for="title">Title *</Label>
                                <Input type="string" name="title" id="title" placeholder="What needs to be done" value={title} onChange={(e) => setTitle(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Input type="string" name="description" id="description" placeholder="Add more details" value={description} onChange={(e) => setDescription(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col xs={12}><FormGroup>
                            <Label for="repIds">Assigned To</Label>
                            <RepsTypeAhead onChange={(selectedRepIds) => { setRepIds(selectedRepIds) }} />
                        </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label for="dueDate">Due Date</Label>
                                <Input type="date" name="dueDate" id="dueDate" placeholder="Due Date" min={new Date().toISOString().split("T")[0]} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                            </FormGroup>
                        </Col>
                        {columnsData && columnsData.allColumns && columnsData.allColumns.edges.map((column, index) => (
                            <Col xs={12} key={index}>
                                <FormGroup>
                                    <Label for={column.node.id}>{column.node.title} {column.node.isRequired ? "*" : ""}</Label>
                                    {column.node.columnType === "FK_OBJECT" ? (
                                        column.node.contentType.model === "driver" ? <DriverTypeAhead onChange={(value) => setColumnValue(column.node.id, value)} />
                                            : column.node.contentType.model === "car" ? <CarTypeAhead onChange={(value) => setColumnValue(column.node.id, value)} />
                                                : column.node.contentType.model === "incident" ? <IncidentTypeAhead onChange={(value) => setColumnValue(column.node.id, value)} value={filterValues?.incidentId || null} />
                                                    : null
                                    ) : column.node.columnType === "LIST" ? (
                                        <RenderListColumnInput column={column.node} />
                                    ) : column.node.columnType === "ENUM" ? (
                                        <Select
                                            options={(column.node.allowedOptions || []).map(opt => ({ value: opt, label: opt }))}
                                            onChange={(selected) => setColumnValue(column.node.id, selected ? selected.value : null)}
                                            isClearable
                                        />
                                    ) : column.node.columnType === "NUMBER" ? (
                                        <Input type="number" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : column.node.columnType === "DECIMAL" ? (
                                        <Input type="number" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : column.node.columnType === "BOOLEAN" ? (
                                        <Input type="checkbox" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : column.node.columnType === "DATE" ? (
                                        <Input type="date" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : column.node.columnType === "TIME" ? (
                                        <Input type="time" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : column.node.columnType === "DATETIME" ? (
                                        <Input type="datetime-local" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    ) : (
                                        <Input type="string" name={column.node.id} id={column.node.id} placeholder={column.node.title} value={column.node.value} onChange={(e) => setColumnValue(column.node.id, e.target.value)} />
                                    )}
                                </FormGroup>
                            </Col>
                        ))}

                    </Row>
                    <br />
                    <ModalFooter>
                        {loading ?
                            <Button color="primary" disabled={true}>Creating Task...</Button> :
                            <Button color="primary" type="submit" disabled={
                                !title ||
                                !boardId ||
                                loading
                            }>Create Task</Button>}
                        <Button color="secondary" onClick={closeModal}>Close</Button>
                    </ModalFooter>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default (withApolloClient(NewTaskModal))
